import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FilePlus2,
  Trash2,
  Search,
  AlertTriangle,
  Check,
  X,
  Loader2,
  MessageSquare,
  ExternalLink,
  Phone,
  Image as ImageIcon,
  Video,
  FileText,
  Sparkles,
  CheckCheck,
  Info,
  Plus,
} from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import CountryCodePicker from "@/components/CountryCodePicker";
import { DEFAULT_DIAL_ISO, findByIso } from "@/lib/countryDialCodes";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { useAuth } from "@/contexts/AuthContext";

interface Template {
  id: string;
  name: string;
  label?: string;
  status: string;
  category?: string;
  type?: string;
  language?: string;
  text?: string;
  totalParameters: number;
  quickReplies?: string[];
  rejectedReason?: string;
}

const CATEGORIES = ["MARKETING", "UTILITY"] as const;
/** All 4 official AiSensy / Meta Template Types */
const TEMPLATE_TYPES = ["TEXT", "IMAGE", "VIDEO", "FILE"] as const;

const STATUS_STYLE: Record<string, string> = {
  APPROVED: "bg-green-50 text-green-700 border-green-200",
  PENDING: "bg-amber-50 text-amber-700 border-amber-200",
  REJECTED: "bg-red-50 text-red-700 border-red-200",
};

/** Count DISTINCT {{n}} placeholders in template text */
const countPlaceholders = (text: string): number =>
  new Set(text.match(/\{\{\s*\d+\s*\}\}/g) ?? []).size;

/**
 * Rewrite placeholders so they run 1..n in order of first appearance.
 *
 * WhatsApp requires an unbroken sequence, so typing {{3}} after a {{1}} has to
 * become {{2}}. Doing it as you type — the way AiSensy's own editor does —
 * means the numbering is never wrong, rather than being wrong until a
 * validation message tells you so.
 *
 * `order` returns the OLD number behind each new position, which is what lets
 * the caller carry sample values across: deleting {{1}} from "{{1}} {{2}}"
 * renumbers {{2}} to {{1}}, and its sample has to move with it or every
 * remaining value silently shifts onto the wrong placeholder.
 */
function renumberPlaceholders(text: string): { text: string; order: string[] } {
  const seen = new Map<string, number>();
  const order: string[] = [];
  const next = text.replace(/\{\{\s*(\d+)\s*\}\}/g, (_match, n: string) => {
    if (!seen.has(n)) {
      seen.set(n, seen.size + 1);
      order.push(n);
    }
    return `{{${seen.get(n)}}}`;
  });
  return { text: next, order };
}

/**
 * WhatsApp's inline formatting markers, longest first so ``` is tested before
 * the single-character markers can claim part of it.
 */
const WA_MARKERS = [
  { marker: "```", tag: "mono" },
  { marker: "*", tag: "bold" },
  { marker: "_", tag: "italic" },
  { marker: "~", tag: "strike" },
] as const;

/**
 * Render text the way WhatsApp will: *bold*, _italic_, ~strikethrough~ and
 * ```monospace```.
 *
 * Showing the raw markers instead makes the preview lie about the final
 * message, which defeats the point of having one — and formatting is the part
 * you most want to eyeball before submitting for review.
 *
 * Two details that mirror real WhatsApp behaviour:
 *  - A marker pair only formats when it wraps non-space content, so "2 * 3 * 4"
 *    stays arithmetic rather than turning into italics.
 *  - Monospace content is NOT parsed further, so a literal asterisk inside a
 *    code span survives.
 */
function renderWhatsAppText(text: string, keyPrefix = ""): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let buffer = "";
  let i = 0;
  let key = 0;

  const flush = () => {
    if (buffer) {
      out.push(buffer);
      buffer = "";
    }
  };

  while (i < text.length) {
    let matched = false;

    for (const { marker, tag } of WA_MARKERS) {
      if (!text.startsWith(marker, i)) continue;

      const start = i + marker.length;
      const close = text.indexOf(marker, start);
      if (close === -1) continue;

      const inner = text.slice(start, close);
      // WhatsApp ignores a pair whose content is empty or edged with spaces.
      if (!inner || /^\s|\s$/.test(inner)) continue;

      flush();
      const k = `${keyPrefix}${key++}`;

      if (tag === "mono") {
        out.push(
          <code key={k} className="font-mono text-[11px] bg-black/5 rounded px-1 py-0.5">
            {inner}
          </code>,
        );
      } else {
        const children = renderWhatsAppText(inner, `${k}-`);
        if (tag === "bold") out.push(<strong key={k}>{children}</strong>);
        else if (tag === "italic") out.push(<em key={k}>{children}</em>);
        else out.push(<s key={k}>{children}</s>);
      }

      i = close + marker.length;
      matched = true;
      break;
    }

    if (!matched) {
      buffer += text[i];
      i += 1;
    }
  }

  flush();
  return out;
}

const EMPTY_FORM = {
  name: "",
  category: "MARKETING" as (typeof CATEGORIES)[number],
  type: "TEXT" as (typeof TEMPLATE_TYPES)[number],
  // AiSensy stores the long form ("English"), not an ISO code — every template
  // in the live catalog uses it. Sending "en" creates a template that can then
  // fail to send on a language mismatch.
  language: "English",
  headerText: "",
  headerImageUrl: "",
  text: "",
  exampleValuesList: ["", "", "", "", ""],
  footerText: "",
  interactiveType: "none" as "none" | "cta" | "quick_reply",
  quickRepliesList: [] as string[],
  ctas: [] as CtaDraft[],
};

/** Meta's real limit for any button label — quick reply or call-to-action. */
const MAX_BUTTON_TITLE = 25;
const MAX_QUICK_REPLIES = 3;

interface CtaDraft {
  /** Stable key for React across reorders and removals. */
  id: string;
  type: "URL" | "PHONE_NUMBER";
  title: string;
  url: string;
  /** ISO code; the dial digits come from the country table. */
  countryIso: string;
  /** National part only — the dial code is prepended at submit. */
  phone: string;
}

const newCta = (type: CtaDraft["type"]): CtaDraft => ({
  id: `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  type,
  title: "",
  url: "",
  countryIso: DEFAULT_DIAL_ISO,
  phone: "",
});

export default function TemplateManager() {
  const { isSuperAdmin } = useAuth();

  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get<any>("/admin/whatsapp/templates?limit=200");
      const items = resp?.items ?? resp?.data?.items ?? [];
      setTemplates(Array.isArray(items) ? items : []);
    } catch (e: any) {
      setError(e?.message || "Could not load templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!showForm) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) setShowForm(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showForm, submitting]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter(
      (t) =>
        (!statusFilter || t.status === statusFilter) &&
        (!q ||
          t.name.toLowerCase().includes(q) ||
          (t.text ?? "").toLowerCase().includes(q)),
    );
  }, [templates, search, statusFilter]);

  const placeholdersCount = countPlaceholders(form.text);

  // Active examples for placeholders
  const activeExamples = useMemo(() => {
    const res: string[] = [];
    for (let i = 0; i < placeholdersCount; i++) {
      res.push(form.exampleValuesList[i]?.trim() || "");
    }
    return res;
  }, [form.exampleValuesList, placeholdersCount]);

  const quickReplies = useMemo(() => {
    if (form.interactiveType !== "quick_reply") return [];
    return form.quickRepliesList.map((v) => v.trim()).filter(Boolean);
  }, [form.interactiveType, form.quickRepliesList]);

  const ctaButtons = useMemo(() => {
    if (form.interactiveType !== "cta") return [];
    return form.ctas.map((c) =>
      c.type === "URL"
        ? { type: "URL" as const, text: c.title.trim(), url: c.url.trim() }
        : {
            type: "PHONE_NUMBER" as const,
            text: c.title.trim(),
            // Dial code + national number, digits only — WhatsApp rejects "+"
            // and a number with no country code.
            phoneNumber: `${findByIso(c.countryIso)?.dial ?? ""}${c.phone.replace(/\D/g, "")}`,
          },
    );
  }, [form.interactiveType, form.ctas]);

  /**
   * Body edits go through renumbering, so placeholders stay 1..n as you type
   * and each sample value follows its placeholder to the new position.
   */
  const onTextChange = (raw: string) => {
    const { text, order } = renumberPlaceholders(raw);
    setForm((prev) => {
      const changed = order.some((oldN, i) => Number(oldN) !== i + 1);
      if (!changed) return { ...prev, text };
      return {
        ...prev,
        text,
        // Follow the value that belonged to this placeholder; if it had none,
        // keep whatever sat at this position. Without the fallback, typing
        // {{3}} where you meant {{2}} would blank a sample you already filled.
        exampleValuesList: order.map(
          (oldN, i) =>
            prev.exampleValuesList[Number(oldN) - 1] || prev.exampleValuesList[i] || "",
        ),
      };
    });
  };

  // Real-time policy validation checks
  const formIssues = useMemo(() => {
    const issues: Array<{ text: string; blocking: boolean }> = [];
    const bad = (text: string) => issues.push({ text, blocking: true });
    const warn = (text: string) => issues.push({ text, blocking: false });

    if (form.name && !/^[a-z0-9_]+$/.test(form.name)) {
      bad("Template Name: only lowercase letters, numbers, and underscores allowed (no spaces or special chars).");
    }

    if (form.text) {
      if (/^\s*\{\{\s*\d+\s*\}\}/.test(form.text)) {
        bad("Template Format: message cannot begin with a variable placeholder.");
      }
      if (/\{\{\s*\d+\s*\}\}\s*$/.test(form.text)) {
        bad("Template Format: message cannot end with a variable placeholder.");
      }
      if (/\{\{\s*\d+\s*\}\}[\s,.;:!?-]*\{\{\s*\d+\s*\}\}/.test(form.text)) {
        bad("Template Format: placeholders cannot sit adjacent to each other without words in between.");
      }
      if (form.text.length > 1024) {
        bad(`Template Format: ${form.text.length} characters — max limit is 1024.`);
      }
      if (/[ \t]+$/m.test(form.text)) {
        bad("Template Format: remove trailing spaces at line ends.");
      }
      if (/\S {2,}\S/.test(form.text)) {
        bad("Template Format: double spaces between words get templates rejected.");
      }
      if (/\n[ \t]*\n/.test(form.text)) {
        bad("Template Format: remove double-line breaks between paragraphs.");
      }
      if (/[ऀ-ॿ]/.test(form.text) && /[a-zA-Z]{3}/.test(form.text)) {
        warn("Hinglish check: mixing Hindi script and English in one template is usually rejected by Meta.");
      }
    }

    const header = form.headerText.trim();
    if (form.type === "TEXT" && header) {
      if (header.length > 60) {
        bad(`Header Text: ${header.length} characters — max limit is 60.`);
      }
      // Meta numbers header variables in their own sequence, separate from the
      // body's, so a header {{1}} is not the body's {{1}}. Keeping headers
      // static avoids a whole class of rejections.
      if (/\{\{\s*\d+\s*\}\}/.test(header)) {
        bad("Header Text: must be static — placeholders are not supported here.");
      }
    }

    const footer = form.footerText.trim();
    if (footer) {
      if (footer.length > 60) {
        bad(`Footer Text: ${footer.length} characters — max limit is 60.`);
      }
      if (/\{\{\s*\d+\s*\}\}/.test(footer)) {
        bad("Footer Text: must be static — placeholders are not supported here.");
      }
    }

    if (form.type !== "TEXT") {
      const media = form.headerImageUrl.trim();
      if (!media) {
        if (form.text.trim()) {
          bad(`${form.type} Header: add a sample media URL — reviewers judge the media too.`);
        }
      } else if (!/^https?:\/\/[^\s]+\.[^\s]+/i.test(media)) {
        bad(`${form.type} Header: must be a full public URL starting with https://.`);
      } else if (
        form.type === "FILE" &&
        !/\.pdf$/i.test(media.split("?")[0])
      ) {
        bad("FILE Header: Meta requires PDF format (.pdf) for document template headers.");
      }
    }

    if (placeholdersCount > 0) {
      const filledCount = activeExamples.filter(Boolean).length;
      if (filledCount < placeholdersCount) {
        bad(`Sample Values: specify sample values for all ${placeholdersCount} parameter(s).`);
      }
    }

    if (form.category === "MARKETING" && form.text && !/\b(stop|unsubscribe|opt[\s-]?out)\b/i.test(form.text)) {
      warn("Marketing Guidance: marketing messages should include opt-out instructions (e.g., 'Reply STOP to opt out').");
    }

    if (form.interactiveType === "quick_reply") {
      if (quickReplies.length > MAX_QUICK_REPLIES) {
        bad(`Quick Replies: WhatsApp permits at most ${MAX_QUICK_REPLIES} buttons.`);
      }
      // Only nag about blank rows once there is a message to go with them —
      // an empty body already blocks submit, so this stays quiet while the
      // template is still being written rather than flashing red on tab click.
      if (form.text.trim()) {
        if (quickReplies.length === 0) {
          bad("Quick Replies: add at least one, or switch to None.");
        } else if (form.quickRepliesList.some((q) => !q.trim())) {
          // A blank row is dropped silently at submit, leaving fewer buttons
          // than the form shows.
          bad("Quick Replies: fill in every row, or remove the empty ones.");
        }
      }
      for (const qr of quickReplies) {
        if (qr.length > MAX_BUTTON_TITLE) {
          bad(`Quick Reply '${qr}': maximum ${MAX_BUTTON_TITLE} characters allowed.`);
        }
        if (/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{FE0F}\u{200D}]/u.test(qr)) {
          bad(`Quick Reply '${qr}': emojis are not allowed in button labels.`);
        }
      }
    }

    if (form.interactiveType === "cta") {
      if (form.ctas.length === 0 && form.text.trim()) {
        bad("Call to Actions: add at least one button, or switch to None.");
      }
      if (form.ctas.filter((c) => c.type === "URL").length > 1) {
        bad("Call to Actions: only one URL button is allowed.");
      }
      if (form.ctas.filter((c) => c.type === "PHONE_NUMBER").length > 1) {
        bad("Call to Actions: only one phone button is allowed.");
      }
      ctaButtons.forEach((cta, ctaIdx) => {
        const draft = form.ctas[ctaIdx];
        // Missing-value checks wait for a body, so a freshly added row doesn't
        // turn the panel red before it has been filled in. Length and format
        // checks below always run — those only fire on text you actually typed.
        if (!cta.text && form.text.trim()) {
          bad(cta.type === "URL" ? "URL CTA requires a Button Title." : "Phone CTA requires a Button Title.");
        }
        if (cta.text.length > MAX_BUTTON_TITLE) {
          bad(`Button Title '${cta.text}': max limit is ${MAX_BUTTON_TITLE} characters.`);
        }
        if (cta.type === "URL") {
          const u = cta.url ?? "";
          if (!u) {
            if (form.text.trim()) bad("URL CTA requires a redirection link.");
          } else if (!/^https?:\/\/[^\s]+\.[^\s]+/i.test(u)) {
            bad("URL CTA: link must be a full valid URL starting with https://.");
          } else if (/\b(bit\.ly|tinyurl|t\.co|goo\.gl|ow\.ly|is\.gd|rebrand\.ly)\b/i.test(u)) {
            bad("URL CTA: shorteners (bit.ly, tinyurl, etc.) are strictly rejected by Meta.");
          }
          // A dynamic link's {{n}} CONTINUES the body's numbering rather than
          // starting its own — with 2 body placeholders the link's is {{3}}.
          const urlVar = u.match(/\{\{\s*(\d+)\s*\}\}/);
          if (urlVar && Number(urlVar[1]) !== placeholdersCount + 1) {
            bad(
              `URL CTA: dynamic link uses {{${urlVar[1]}}}, but it must be {{${placeholdersCount + 1}}} — link variables continue the message's numbering.`,
            );
          }
        } else {
          // The dial code is always present, so judge the national part on its
          // own — otherwise "no number" never fires and a missing number would
          // be reported as a missing country code, which is just confusing.
          const national = (draft?.phone ?? "").replace(/\D/g, "");
          const full = (cta.phoneNumber ?? "").replace(/\D/g, "");
          if (!national) {
            if (form.text.trim()) bad("Phone CTA requires a phone number.");
          } else if (full.length < 10 || full.length > 15) {
            bad(
              `Phone CTA: +${full} is ${full.length} digits — check the number and country code.`,
            );
          }
        }
      });
      // Two buttons sharing a title is a rejection, and it's easy to miss when
      // the two fields sit in separate rows.
      const titles = ctaButtons.map((b) => b.text.trim().toLowerCase()).filter(Boolean);
      if (titles.length === 2 && titles[0] === titles[1]) {
        bad(`Both buttons are titled '${ctaButtons[0].text}' — each must be different.`);
      }
    }

    return issues;
    // form.type and form.headerImageUrl are read above — without them here the
    // media checks keep showing the previous type's result until something
    // else in the form changes.
  }, [form.name, form.text, form.category, form.type, form.headerText, form.footerText, form.headerImageUrl, form.interactiveType, form.ctas, placeholdersCount, activeExamples, quickReplies, ctaButtons]);

  const blockingIssues = formIssues.filter((i) => i.blocking);
  const canSubmit = !!form.name.trim() && !!form.text.trim() && blockingIssues.length === 0 && !submitting;

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const resp = await api.post<any>("/admin/whatsapp/templates", {
        name: form.name.trim(),
        category: form.category,
        type: form.type,
        language: form.language,
        headerType: form.type,
        headerMediaUrl:
          form.type !== "TEXT" ? form.headerImageUrl.trim() || undefined : undefined,
        headerText: form.type === "TEXT" ? form.headerText.trim() || undefined : undefined,
        footer: form.footerText.trim() || undefined,
        text: form.text.trim(),
        exampleValues: activeExamples,
        quickReplies: form.interactiveType === "quick_reply" ? quickReplies : [],
        ctaButtons: form.interactiveType === "cta" ? ctaButtons : [],
      });
      const created = resp?.data ?? resp;
      setNotice(
        `Submitted "${created?.name ?? form.name}" to WhatsApp for approval (status: PENDING). Review typically takes a few minutes up to 48 hours.`,
      );
      setShowForm(false);
      setForm(EMPTY_FORM);
      await load();
    } catch (e: any) {
      setError(e?.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (t: Template) => {
    const ok = window.confirm(
      `Delete "${t.name}" from WhatsApp?\n\nThis removes it from the provider catalog and cannot be undone.`,
    );
    if (!ok) return;

    setDeletingId(t.id);
    setError(null);
    setNotice(null);
    try {
      await api.delete(`/admin/whatsapp/templates/${t.id}`);
      setTemplates((prev) => prev.filter((x) => x.id !== t.id));
      setNotice(`Deleted "${t.name}".`);
    } catch (e: any) {
      setError(e?.message || "Could not delete");
    } finally {
      setDeletingId(null);
    }
  };

  // Preview text rendering sample parameters in brackets [Sample]
  const previewFormattedText = useMemo(() => {
    if (!form.text.trim()) return "Hey {{1}}, your template message preview will appear here in real time as you fill out the form!";
    let res = form.text;
    const matches = form.text.match(/\{\{\s*\d+\s*\}\}/g) ?? [];
    const unique = Array.from(new Set(matches));
    unique.forEach((m, idx) => {
      const sampleVal = activeExamples[idx] || `Param ${idx + 1}`;
      res = res.replace(new RegExp(m.replace(/[{}]/g, "\\$&"), "g"), `[${sampleVal}]`);
    });
    return res;
  }, [form.text, activeExamples]);

  if (!isSuperAdmin) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
      <div className="flex flex-col items-start gap-4 mb-4 sm:flex-row sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
            <MessageSquare className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">WhatsApp Template Messages</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Create, customize and submit official WhatsApp Template Messages for approval via AiSensy.
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-sm"
        >
          <FilePlus2 className="h-4 w-4" />
          {showForm ? "Cancel" : "+ New Template Message"}
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {notice && (
        <div className="mb-4 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
          {notice}
        </div>
      )}

      {/* AiSensy-Styled Add Template Modal */}
      {showForm && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-6"
          onClick={(e) => {
            if (e.target === e.currentTarget && !submitting) setShowForm(false);
          }}
        >
          <div className="bg-white rounded-xl w-full max-w-5xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden border border-gray-100">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 bg-emerald-700 text-white">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-white/20 flex items-center justify-center backdrop-blur-xs">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold tracking-tight">New Template Message</h2>
                  <p className="text-xs text-emerald-100">
                    Customize and submit template messages for WhatsApp approval via AiSensy
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowForm(false)}
                disabled={submitting}
                className="text-emerald-100 hover:text-white p-1 rounded-md hover:bg-emerald-800 transition-colors disabled:opacity-50"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content — 2 Column Layout (Form + Preview) */}
            <div className="flex-1 overflow-y-auto p-6 grid grid-cols-1 lg:grid-cols-12 gap-6 bg-slate-50/50">
              
              {/* Left Column: Form Controls (7 cols) */}
              <div className="lg:col-span-7 space-y-5 bg-white p-5 rounded-lg border border-gray-200 shadow-xs">
                
                {/* 1. Template Name, Template Category & Template Type */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="flex flex-col justify-between">
                    <div>
                      <label className="block text-xs font-semibold text-gray-800 mb-1">
                        Template Name
                      </label>
                      <p className="text-[11px] text-gray-500 mb-2 min-h-[28px] leading-snug">
                        Lowercase alphanumeric & underscores only.
                      </p>
                    </div>
                    <input
                      value={form.name}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"),
                        })
                      }
                      placeholder="e.g. reengage_hostel_life"
                      className="w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>

                  <div className="flex flex-col justify-between">
                    <div>
                      <label className="block text-xs font-semibold text-gray-800 mb-1">
                        Template Category
                      </label>
                      <p className="text-[11px] text-gray-500 mb-2 min-h-[28px] leading-snug">
                        Marketing or Utility category.
                      </p>
                    </div>
                    <select
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value as any })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-medium"
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-col justify-between">
                    <div>
                      <label className="block text-xs font-semibold text-gray-800 mb-1">
                        Template Type
                      </label>
                      <p className="text-[11px] text-gray-500 mb-2 min-h-[28px] leading-snug">
                        Header media type.
                      </p>
                    </div>
                    <select
                      value={form.type}
                      onChange={(e) => setForm({ ...form, type: e.target.value as any })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-medium"
                    >
                      {TEMPLATE_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Sample Media Header URL field when Template Type is IMAGE, VIDEO, or FILE */}
                {form.type !== "TEXT" && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-semibold text-gray-800">
                        Sample Media URL ({form.type} Header)
                      </label>
                      <span className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">
                        {form.type === "IMAGE" && "Max Size: 5 MB"}
                        {form.type === "VIDEO" && "Max Size: 16 MB"}
                        {form.type === "FILE" && "Max Size: 100 MB (PDF Only)"}
                      </span>
                    </div>
                    <input
                      value={form.headerImageUrl}
                      onChange={(e) => setForm({ ...form, headerImageUrl: e.target.value })}
                      placeholder={
                        form.type === "IMAGE"
                          ? "https://example.com/banner.jpg"
                          : form.type === "VIDEO"
                          ? "https://example.com/video.mp4"
                          : "https://example.com/document.pdf"
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">
                      Provide a sample {form.type === "FILE" ? "PDF document" : form.type.toLowerCase()} URL (Max:{" "}
                      {form.type === "IMAGE" ? "5 MB" : form.type === "VIDEO" ? "16 MB" : "100 MB PDF"}) to illustrate your header media during WhatsApp review.
                    </p>
                  </div>
                )}

                {/* Header text — TEXT templates only; the media types use the
                    media header above instead. */}
                {form.type === "TEXT" && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-semibold text-gray-800">
                        Template Header Text{" "}
                        <span className="font-normal text-gray-500">(Optional)</span>
                      </label>
                      <span
                        className={cn(
                          "text-xs font-mono font-medium",
                          form.headerText.length > 60 ? "text-red-600" : "text-gray-500",
                        )}
                      >
                        {form.headerText.length}/60
                      </span>
                    </div>
                    <input
                      value={form.headerText}
                      onChange={(e) => setForm({ ...form, headerText: e.target.value })}
                      placeholder="e.g. Your seat is still open"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">
                      Shown in bold above the message. Up to 60 characters, static
                      text only — no placeholders.
                    </p>
                  </div>
                )}

                {/* 3. Template Format (Body Content) */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-semibold text-gray-800">
                      Template Format
                    </label>
                    <span
                      className={cn(
                        "text-xs font-mono font-medium",
                        form.text.length > 1024 ? "text-red-600" : "text-gray-500",
                      )}
                    >
                      {form.text.length}/1024
                    </span>
                  </div>
                  <textarea
                    value={form.text}
                    onChange={(e) => onTextChange(e.target.value)}
                    rows={4}
                    placeholder={`Hey {{1}},\n\nThe day is finally here😍!\n{{2}} is officially launched & available on our website 🔥.\n\nPlace your order today for guaranteed shipping in {{3}}⚡!`}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-sans"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">
                    Formatting tips: <code>*bold*</code> · <code>_italic_</code> · <code>~strikethrough~</code> · <code>```monospace```</code>
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    Placeholders renumber themselves — add {"{{5}}"} after {"{{1}}"} and
                    it becomes {"{{2}}"}.
                  </p>
                </div>

                {/* Footer text — greyed small print under the message */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-semibold text-gray-800">
                      Template Footer Text{" "}
                      <span className="font-normal text-gray-500">(Optional)</span>
                    </label>
                    <span
                      className={cn(
                        "text-xs font-mono font-medium",
                        form.footerText.length > 60 ? "text-red-600" : "text-gray-500",
                      )}
                    >
                      {form.footerText.length}/60
                    </span>
                  </div>
                  <input
                    value={form.footerText}
                    onChange={(e) => setForm({ ...form, footerText: e.target.value })}
                    placeholder="e.g. Gyanplug Counseling"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">
                    Small grey line under the message. Up to 60 characters, static
                    text only — no placeholders.
                  </p>
                </div>

                {/* 4. Sample Values Inputs */}
                {placeholdersCount > 0 && (
                  <div className="bg-emerald-50/50 p-3.5 rounded-md border border-emerald-100 space-y-2">
                    <label className="block text-xs font-semibold text-emerald-900">
                      Sample Values
                    </label>
                    <p className="text-[11px] text-emerald-700 mb-2">
                      Specify sample values for your parameters. These values will be displayed during review and can be changed at sending time.
                    </p>
                    <div className="space-y-2">
                      {Array.from({ length: placeholdersCount }).map((_, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <span className="text-xs font-mono font-bold text-emerald-800 w-12 shrink-0">
                            {`{{${idx + 1}}}`}
                          </span>
                          <input
                            value={form.exampleValuesList[idx] || ""}
                            onChange={(e) => {
                              const updated = [...form.exampleValuesList];
                              updated[idx] = e.target.value;
                              setForm({ ...form, exampleValuesList: updated });
                            }}
                            placeholder={
                              idx === 0 ? "Shivam" : idx === 1 ? "Headway Bassheads" : `Sample value ${idx + 1}`
                            }
                            className="flex-1 px-3 py-1.5 text-sm border border-emerald-200 rounded-md bg-white focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 5. Interactive Actions */}
                <div className="pt-3 border-t border-gray-200">
                  <label className="block text-xs font-semibold text-gray-800 mb-1">
                    Interactive Actions
                  </label>
                  <p className="text-[11px] text-gray-500 mb-2">
                    In addition to your message, you can send interactive
                    call-to-action buttons or quick replies — one kind or the other,
                    not both.
                  </p>

                  {/* One card: the mode selector and its options share a border,
                      so the choice reads as owning the panel underneath it
                      rather than floating above an unrelated box. */}
                  <div className="rounded-md border border-gray-200 overflow-hidden bg-white">
                    <div
                      role="tablist"
                      aria-label="Interactive action type"
                      className="flex gap-1 p-1 bg-gray-50 border-b border-gray-200"
                    >
                      {(
                        [
                          ["none", "None"],
                          ["cta", "Call to Actions"],
                          ["quick_reply", "Quick Replies"],
                        ] as const
                      ).map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          role="tab"
                          aria-selected={form.interactiveType === value}
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              interactiveType: value,
                              // Start the chosen kind with one empty row, so
                              // picking a tab doesn't greet you with a "add at
                              // least one" error before you can act on it.
                              ctas:
                                value === "cta" && prev.ctas.length === 0
                                  ? [newCta("URL")]
                                  : prev.ctas,
                              quickRepliesList:
                                value === "quick_reply" && prev.quickRepliesList.length === 0
                                  ? [""]
                                  : prev.quickRepliesList,
                            }))
                          }
                          className={cn(
                            "flex-1 px-3 py-1.5 text-xs font-medium rounded transition-colors",
                            form.interactiveType === value
                              ? "bg-white text-emerald-700 shadow-sm ring-1 ring-gray-200"
                              : "text-gray-600 hover:text-gray-900 hover:bg-white/60",
                          )}
                        >
                          {label}
                        </button>
                      ))}
                    </div>

                    {form.interactiveType === "none" && (
                      <p className="px-3 py-3 text-[11px] text-gray-500">
                        The message will be sent without buttons.
                      </p>
                    )}

                  {/* CTA Buttons — up to 2, one URL and one phone */}
                  {form.interactiveType === "cta" && (
                    <div>
                      {form.ctas.length === 0 && (
                        <p className="px-3 py-3 text-[11px] text-gray-500">
                          No call-to-action buttons yet. You can add one link button
                          and one call button.
                        </p>
                      )}

                      <div className="divide-y divide-gray-100">
                      {form.ctas.map((cta, idx) => {
                        const patch = (changes: Partial<CtaDraft>) =>
                          setForm((prev) => ({
                            ...prev,
                            ctas: prev.ctas.map((c, i) =>
                              i === idx ? { ...c, ...changes } : c,
                            ),
                          }));

                        // A type already used by the OTHER button is not
                        // offered — WhatsApp allows only one of each, so
                        // letting you pick it would only create an error.
                        const takenByOther = form.ctas
                          .filter((_, i) => i !== idx)
                          .map((c) => c.type);

                        return (
                          <div
                            key={cta.id}
                            className="px-3 py-3 space-y-2"
                          >
                            <div className="flex items-center gap-2">
                              <select
                                value={cta.type}
                                onChange={(e) =>
                                  patch({ type: e.target.value as CtaDraft["type"] })
                                }
                                className="px-2 py-1.5 text-xs font-medium border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-emerald-500"
                              >
                                <option value="URL" disabled={takenByOther.includes("URL")}>
                                  URL
                                </option>
                                <option
                                  value="PHONE_NUMBER"
                                  disabled={takenByOther.includes("PHONE_NUMBER")}
                                >
                                  Phone Number
                                </option>
                              </select>
                              <span className="text-[11px] text-gray-400 flex-1">
                                Action {idx + 1}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  setForm((prev) => ({
                                    ...prev,
                                    ctas: prev.ctas.filter((_, i) => i !== idx),
                                  }))
                                }
                                className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                                aria-label="Remove this action"
                                title="Remove"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <div>
                                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                                  Button Title
                                </label>
                                <div className="flex items-center gap-2">
                                  <input
                                    value={cta.title}
                                    onChange={(e) => patch({ title: e.target.value })}
                                    maxLength={MAX_BUTTON_TITLE}
                                    placeholder={cta.type === "URL" ? "See options" : "Talk to us"}
                                    className="flex-1 min-w-0 px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-emerald-500"
                                  />
                                  <span
                                    className={cn(
                                      "text-[10px] font-mono w-10 shrink-0 text-right",
                                      cta.title.length === MAX_BUTTON_TITLE
                                        ? "text-amber-600"
                                        : "text-gray-400",
                                    )}
                                  >
                                    {cta.title.length}/{MAX_BUTTON_TITLE}
                                  </span>
                                </div>
                              </div>

                              {cta.type === "URL" ? (
                                <div>
                                  <label className="block text-[11px] font-medium text-gray-700 mb-1">
                                    Button Value (URL)
                                  </label>
                                  <input
                                    value={cta.url}
                                    onChange={(e) => patch({ url: e.target.value })}
                                    placeholder="https://gyanplug.com/colleges"
                                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-emerald-500"
                                  />
                                </div>
                              ) : (
                                <div>
                                  <label className="block text-[11px] font-medium text-gray-700 mb-1">
                                    Country &amp; Phone Number
                                  </label>
                                  <div className="flex gap-1.5">
                                    <CountryCodePicker
                                      value={cta.countryIso}
                                      onChange={(iso) => patch({ countryIso: iso })}
                                      className="w-28 shrink-0"
                                    />
                                    <input
                                      value={cta.phone}
                                      onChange={(e) =>
                                        patch({ phone: e.target.value.replace(/\D/g, "") })
                                      }
                                      placeholder="9876543210"
                                      className="flex-1 min-w-0 px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-emerald-500"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      </div>

                      {/* Hidden once both slots are used, rather than shown
                          disabled — there is nothing left to add. */}
                      {form.ctas.length < 2 && (
                        <div className="px-3 py-2.5 bg-gray-50/70 border-t border-gray-100">
                          <button
                            type="button"
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                ctas: [
                                  ...prev.ctas,
                                  newCta(
                                    prev.ctas.some((c) => c.type === "URL")
                                      ? "PHONE_NUMBER"
                                      : "URL",
                                  ),
                                ],
                              }))
                            }
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-dashed border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add Call to Action
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Quick Replies — up to 3, added one at a time */}
                  {form.interactiveType === "quick_reply" && (
                    <div>
                      <p className="px-3 pt-3 pb-2 text-[11px] text-gray-500">
                        Up to {MAX_QUICK_REPLIES} quick replies, max {MAX_BUTTON_TITLE}{" "}
                        characters each, no emojis.
                      </p>

                      {form.quickRepliesList.map((qr, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 px-3 py-2 border-t border-gray-100"
                        >
                          <span className="text-xs text-gray-600 w-20 shrink-0 font-medium">
                            Reply {idx + 1}
                          </span>
                          <input
                            value={qr}
                            onChange={(e) => {
                              const updated = [...form.quickRepliesList];
                              updated[idx] = e.target.value;
                              setForm({ ...form, quickRepliesList: updated });
                            }}
                            maxLength={MAX_BUTTON_TITLE}
                            placeholder={
                              idx === 0
                                ? "I'm interested"
                                : idx === 1
                                ? "Not right now"
                                : "Tell me more"
                            }
                            className="flex-1 min-w-0 px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-emerald-500"
                          />
                          <span
                            className={cn(
                              "text-[10px] font-mono w-10 shrink-0 text-right",
                              qr.length === MAX_BUTTON_TITLE
                                ? "text-amber-600"
                                : "text-gray-400",
                            )}
                          >
                            {qr.length}/{MAX_BUTTON_TITLE}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                quickRepliesList: prev.quickRepliesList.filter(
                                  (_, i) => i !== idx,
                                ),
                              }))
                            }
                            className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                            aria-label={`Remove quick reply ${idx + 1}`}
                            title="Remove"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}

                      {form.quickRepliesList.length < MAX_QUICK_REPLIES && (
                        <div className="px-3 py-2.5 bg-gray-50/70 border-t border-gray-100">
                          <button
                            type="button"
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                quickRepliesList: [...prev.quickRepliesList, ""],
                              }))
                            }
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-dashed border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add Quick Reply
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  </div>
                </div>

                {/* Policy Validation Alerts */}
                {formIssues.length > 0 && (
                  <div className="p-3 rounded-md bg-amber-50 border border-amber-200 space-y-1.5">
                    <p className="text-xs font-semibold text-amber-900 flex items-center gap-1.5">
                      <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                      Policy & Format Checks
                    </p>
                    <ul className="space-y-1">
                      {formIssues.map((issue, i) => (
                        <li
                          key={i}
                          className={cn(
                            "text-xs flex items-start gap-1.5",
                            issue.blocking ? "text-red-700 font-medium" : "text-amber-800",
                          )}
                        >
                          <span className="shrink-0">•</span>
                          {issue.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Right Column: WhatsApp Live Smartphone Preview Card (5 cols) */}
              <div className="lg:col-span-5 flex flex-col items-center">
                <div className="w-full max-w-sm sticky top-0">
                  
                  {/* WhatsApp Mobile Frame Header */}
                  <div className="bg-[#075E54] text-white px-4 py-3 rounded-t-2xl shadow-md flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center">
                      <MessageSquare className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h4 className="text-xs font-semibold">AiSensy Business Preview</h4>
                      <p className="text-[10px] text-emerald-100">WhatsApp Template Card</p>
                    </div>
                  </div>

                  {/* Chat Container Background */}
                  {/* Content stacks from the top. `justify-between` here spread
                      the bubble and its buttons to opposite ends of the 380px
                      frame, which is not how WhatsApp draws them — buttons sit
                      directly under the message. */}
                  <div className="bg-[#E5DDD5] p-4 rounded-b-2xl shadow-inner min-h-[380px] flex flex-col border-x border-b border-gray-300">
                    
                    {/* Chat Bubble Card */}
                    {/* Bubble and buttons share one column so the buttons match
                        the bubble's width, the way WhatsApp renders them. */}
                    <div className="max-w-[92%] self-start space-y-0.5">
                    <div className="bg-white rounded-lg p-3 shadow-md border border-gray-200/80 space-y-2.5 relative">
                      
                      {/* Media Header Preview for IMAGE, VIDEO, or FILE */}
                      {form.type !== "TEXT" && (
                        <div className="rounded-md overflow-hidden bg-slate-100 border border-gray-200 h-36 flex items-center justify-center text-gray-400">
                          {form.headerImageUrl ? (
                            form.type === "IMAGE" ? (
                              <img
                                src={form.headerImageUrl}
                                alt="Header sample"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLElement).style.display = "none";
                                }}
                              />
                            ) : form.type === "VIDEO" ? (
                              <video
                                src={form.headerImageUrl}
                                className="w-full h-full object-cover"
                                controls
                              />
                            ) : (
                              <div className="flex flex-col items-center gap-1 p-3 text-center">
                                <FileText className="h-8 w-8 text-emerald-600" />
                                <span className="text-xs font-medium text-gray-700 truncate max-w-[200px]">
                                  {form.headerImageUrl.split("/").pop() || "Document.pdf"}
                                </span>
                              </div>
                            )
                          ) : (
                            <div className="flex flex-col items-center gap-1">
                              {form.type === "IMAGE" && <ImageIcon className="h-8 w-8 text-gray-400" />}
                              {form.type === "VIDEO" && <Video className="h-8 w-8 text-gray-400" />}
                              {form.type === "FILE" && <FileText className="h-8 w-8 text-gray-400" />}
                              <span className="text-[11px] text-gray-500">Header {form.type} Sample</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Header Text — bold line above the body */}
                      {form.type === "TEXT" && form.headerText.trim() && (
                        <h5 className="font-bold text-xs text-gray-900 break-words">
                          {form.headerText}
                        </h5>
                      )}

                      {/* Message Body with Sample Values Replaced in [Brackets] */}
                      <p className="text-xs text-gray-800 leading-relaxed whitespace-pre-wrap break-words">
                        {renderWhatsAppText(previewFormattedText)}
                      </p>

                      {/* Footer — WhatsApp renders it small and grey */}
                      {form.footerText.trim() && (
                        <p className="text-[10px] text-gray-500 break-words">
                          {form.footerText}
                        </p>
                      )}

                      {/* Timestamp & Double Tick */}
                      <div className="flex items-center justify-end gap-1 text-[9px] text-gray-400 pt-0.5">
                        <span>12:45 PM</span>
                        <CheckCheck className="h-3 w-3 text-blue-500" />
                      </div>
                    </div>

                    {/* Interactive Buttons Preview (Rendered below bubble) */}
                    <div className="space-y-0.5">
                      {/* CTA buttons, in the order they were added */}
                      {form.interactiveType === "cta" &&
                        form.ctas
                          .filter((c) => c.title.trim())
                          .map((c) => (
                            <div
                              key={c.id}
                              className="bg-white hover:bg-slate-50 text-emerald-600 text-xs font-semibold py-2 px-3 rounded-lg text-center shadow-xs border border-gray-200 flex items-center justify-center gap-1.5 cursor-pointer"
                            >
                              {c.type === "URL" ? (
                                <ExternalLink className="h-3.5 w-3.5" />
                              ) : (
                                <Phone className="h-3.5 w-3.5" />
                              )}
                              {c.title}
                            </div>
                          ))}

                      {/* Quick Replies Buttons */}
                      {form.interactiveType === "quick_reply" &&
                        quickReplies.map((qr, i) => (
                          <div
                            key={i}
                            className="bg-white hover:bg-slate-50 text-emerald-600 text-xs font-semibold py-2 px-3 rounded-lg text-center shadow-xs border border-gray-200 cursor-pointer"
                          >
                            {qr}
                          </div>
                        ))}
                    </div>
                    </div>

                    {/* mt-auto keeps the caption pinned to the bottom of the
                        frame without pushing the message down with it. */}
                    <div className="mt-auto pt-4 text-center">
                      <span className="inline-flex items-center gap-1 text-[10px] text-gray-500 bg-white/80 px-2 py-0.5 rounded-full border border-gray-200">
                        <Info className="h-3 w-3 text-emerald-600" />
                        Live WhatsApp rendering preview
                      </span>
                    </div>

                  </div>
                </div>
              </div>

            </div>

            {/* Modal Footer Controls */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
              <div className="text-xs text-gray-500">
                {blockingIssues.length > 0 ? (
                  <span className="text-red-600 font-medium flex items-center gap-1">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Please fix {blockingIssues.length} issue(s) before submitting
                  </span>
                ) : !form.name.trim() || !form.text.trim() ? (
                  // Without this branch the footer claimed "Ready to submit" on a
                  // blank form, next to a disabled button.
                  <span className="flex items-center gap-1">
                    Add a template name and message to continue
                  </span>
                ) : (
                  <span className="text-emerald-700 font-medium flex items-center gap-1">
                    <Check className="h-3.5 w-3.5" />
                    Ready to submit to WhatsApp for approval
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowForm(false)}
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submit}
                  disabled={!canSubmit}
                  className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                  {submitting ? "Submitting for approval..." : "Submit for Approval"}
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Templates Table List */}
      <div className="flex gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search templates by name or content..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white"
        >
          <option value="">All statuses</option>
          <option value="APPROVED">Approved</option>
          <option value="PENDING">Pending</option>
          <option value="REJECTED">Rejected</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <LoadingSpinner />
        </div>
      ) : visible.length === 0 ? (
        <p className="text-center py-10 text-sm text-gray-500">
          No templates match your search.
        </p>
      ) : (
        <ul className="border border-gray-200 rounded-md divide-y divide-gray-100 max-h-96 overflow-y-auto">
          {visible.map((t) => (
            <li key={t.id} className="p-3 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-sm font-mono font-medium text-gray-900">{t.name}</span>
                    <span
                      className={cn(
                        "text-[11px] px-2 py-0.5 rounded font-semibold border",
                        STATUS_STYLE[t.status] ??
                          "bg-gray-100 text-gray-600 border-gray-200",
                      )}
                    >
                      {t.status}
                    </span>
                    {t.category && (
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-medium">
                        {t.category}
                      </span>
                    )}
                    {t.type && (
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                        {t.type}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 line-clamp-2 whitespace-pre-wrap">
                    {t.text}
                  </p>
                  {t.status === "REJECTED" && t.rejectedReason && (
                    <p className="text-xs text-red-600 mt-1 font-medium">
                      Rejected reason: {t.rejectedReason}
                    </p>
                  )}
                  {!!t.quickReplies?.length && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {t.quickReplies.map((q, i) => (
                        <span
                          key={i}
                          className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 font-medium"
                        >
                          {q}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => remove(t)}
                  disabled={deletingId === t.id}
                  title="Delete from WhatsApp"
                  className="shrink-0 p-2 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
                >
                  {deletingId === t.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
